import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { UserCog, UserPlus, Search, MoreHorizontal, Loader2, Mail, Copy, Trash2, ShieldCheck, Ban, RefreshCw } from "lucide-react";

const STATUS_COLORS = { active: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600", disabled: "border-red-500/40 bg-red-500/10 text-red-600", pending: "border-amber-500/40 bg-amber-500/10 text-amber-600" };

export default function Users() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canManage = (user?.permissions || []).includes("users.manage");
  const canDelete = (user?.permissions || []).includes("users.delete");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["users", search, roleFilter],
    queryFn: async () => {
      const params = {};
      if (search) params.search = search;
      if (roleFilter !== "all") params.role = roleFilter;
      return (await api.get("/users", { params })).data.data;
    },
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["users"] });

  const updateUser = async (id, payload) => {
    try { await api.patch(`/users/${id}`, payload); toast.success("Utente aggiornato"); refresh(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const doDelete = async () => {
    try { await api.delete(`/users/${deleteTarget.id}`); toast.success("Utente eliminato"); setDeleteTarget(null); refresh(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const resend = async (id) => {
    try { const { data } = await api.post(`/users/invitations/${id}/resend`); toast.success("Invito reinviato"); if (data.data?.invite_link) navigator.clipboard?.writeText(window.location.origin + data.data.invite_link); }
    catch (e) { toast.error(apiError(e)); }
  };
  const cancelInvite = async (id) => {
    try { await api.delete(`/users/invitations/${id}`); toast.success("Invito annullato"); refresh(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const roles = data?.roles || [];

  return (
    <div>
      <PageHeader title="Utenti" subtitle="Gestisci il team e i permessi del tuo tenant" icon={UserCog}
        actions={canManage && <Button className="rounded-lg" onClick={() => setInviteOpen(true)} data-testid="invite-user-btn"><UserPlus className="mr-1 h-4 w-4" /> Invita utente</Button>} />

      <div className="mb-4 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Cerca per nome o email..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="user-search" />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-full sm:w-48" data-testid="role-filter"><SelectValue placeholder="Ruolo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i ruoli</SelectItem>
            {roles.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="overflow-hidden" data-testid="users-table">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Utente</TableHead>
                <TableHead>Ruolo</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead>Ultimo accesso</TableHead>
                <TableHead>Aggiunto</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 4 }).map((_, i) => <TableRow key={i}><TableCell colSpan={6}><Skeleton className="h-10 w-full" /></TableCell></TableRow>)
              ) : (
                <>
                  {data?.users.map((u) => (
                    <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-9 w-9"><AvatarImage src={u.avatar_url} /><AvatarFallback className="bg-primary/10 text-primary text-xs">{(u.first_name?.[0] || "") + (u.last_name?.[0] || "")}</AvatarFallback></Avatar>
                          <div><p className="text-sm font-medium">{u.first_name} {u.last_name}</p><p className="text-xs text-muted-foreground">{u.email}</p></div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {canManage && u.role !== "owner" ? (
                          <Select value={u.role} onValueChange={(v) => updateUser(u.id, { role: v })}>
                            <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
                            <SelectContent>{roles.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : <Badge variant="secondary">{u.role_label}</Badge>}
                      </TableCell>
                      <TableCell><Badge variant="outline" className={STATUS_COLORS[u.status]}>{u.status === "active" ? "Attivo" : "Disattivato"}</Badge></TableCell>
                      <TableCell className="text-sm text-muted-foreground">{u.last_login_at ? new Date(u.last_login_at).toLocaleDateString("it-IT") : "—"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{new Date(u.created_at).toLocaleDateString("it-IT")}</TableCell>
                      <TableCell className="text-right">
                        {canManage && u.role !== "owner" && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`user-actions-${u.id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => updateUser(u.id, { status: u.status === "active" ? "disabled" : "active" })}>
                                {u.status === "active" ? <><Ban className="mr-2 h-4 w-4" /> Disattiva</> : <><ShieldCheck className="mr-2 h-4 w-4" /> Attiva</>}
                              </DropdownMenuItem>
                              {canDelete && <DropdownMenuItem className="text-destructive" onClick={() => setDeleteTarget(u)}><Trash2 className="mr-2 h-4 w-4" /> Elimina</DropdownMenuItem>}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {data?.invitations.map((inv) => (
                    <TableRow key={inv.id} className="bg-muted/30" data-testid={`invite-row-${inv.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-9 w-9"><AvatarFallback className="bg-amber-500/10 text-amber-600"><Mail className="h-4 w-4" /></AvatarFallback></Avatar>
                          <div><p className="text-sm font-medium">{inv.first_name} {inv.last_name}</p><p className="text-xs text-muted-foreground">{inv.email}</p></div>
                        </div>
                      </TableCell>
                      <TableCell><Badge variant="secondary">{inv.role}</Badge></TableCell>
                      <TableCell><Badge variant="outline" className={STATUS_COLORS.pending}>Invito in sospeso</Badge></TableCell>
                      <TableCell className="text-sm text-muted-foreground">—</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{new Date(inv.created_at).toLocaleDateString("it-IT")}</TableCell>
                      <TableCell className="text-right">
                        {canManage && (
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={() => resend(inv.id)} title="Reinvia"><RefreshCw className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => cancelInvite(inv.id)} title="Annulla"><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} roles={roles} onDone={refresh} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare l'utente?</AlertDialogTitle>
            <AlertDialogDescription>Questa azione rimuoverà {deleteTarget?.email} dal tenant. Non è reversibile.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-delete-user">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function InviteDialog({ open, onOpenChange, roles, onDone }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", role: "operator", message: "", expires_in_days: 7 });
  const [loading, setLoading] = useState(false);
  const [link, setLink] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/users/invite", form);
      toast.success("Invito inviato");
      setLink(data.data?.invite_link ? window.location.origin + data.data.invite_link : null);
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setLink(null); setForm({ first_name: "", last_name: "", email: "", role: "operator", message: "", expires_in_days: 7 }); } }}>
      <DialogContent data-testid="invite-dialog">
        <DialogHeader><DialogTitle>Invita un utente</DialogTitle></DialogHeader>
        {link ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
              <p className="text-sm font-medium">Invito creato!</p>
              <p className="mt-1 text-xs text-muted-foreground">Link di invito (modalità sviluppo):</p>
              <div className="mt-2 flex gap-2">
                <Input readOnly value={link} className="text-xs" />
                <Button size="icon" variant="outline" onClick={() => { navigator.clipboard?.writeText(link); toast.success("Copiato"); }}><Copy className="h-4 w-4" /></Button>
              </div>
            </div>
            <Button className="w-full rounded-lg" onClick={() => onOpenChange(false)}>Chiudi</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Nome</Label><Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="invite-firstname" /></div>
              <div className="space-y-2"><Label>Cognome</Label><Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="invite-lastname" /></div>
            </div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="invite-email" /></div>
            <div className="space-y-2">
              <Label>Ruolo</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>{roles.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Messaggio (opzionale)</Label><Textarea value={form.message} onChange={(e) => set("message", e.target.value)} rows={2} /></div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
              <Button onClick={submit} disabled={loading} className="rounded-lg" data-testid="send-invite-btn">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Invia invito
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
